import { getTheme, mergeStyleSets, Separator, Stack } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPDefaultButton, IPFontIcon, IPLabel, IPText } from 'baseComponents';
import { IPDropdown, IPDropdownCustomStyles } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import GenericModal from 'components/Dialogs/GenericModal';
import InfoMessage from 'components/Notification/InfoMessage';
import { globalStackTokensGapLargePlus5, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { IntegrationMinimumSchedule } from 'models/integrations/integrationTypeHandler';
import RecurringPattern, {
  absoluteYearCycle,
  relativeYearCycle,
  repeatIntervalType,
  weekly,
} from 'models/recurringPattern';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { days, getAuditStart, weekDays1 } from 'utils/datetime';
import LocalizedDatePicker from '../Pickers/LocalizedDatePicker';
import RecurringRelativeOptions from './RecurringRelativeOptions';
import { RecurringPatternSummaryStyle, RecurringSummary } from './RecurringSummary';

const theme = getTheme();
const contentStyles = mergeStyleSets({
  daySelectionContainer: {
    width: '2rem',
    height: '2rem',
    borderRadius: '3px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.themeLight,
    borderColor: theme.palette.themeLight,
    color: theme.palette.themeDark,
    '&:hover': {
      cursor: 'default',
    },
  },
  active: {
    backgroundColor: theme.palette.themeDark,
    borderColor: theme.palette.themeDark,
    color: theme.palette.themeLight,
  },
});

const repeatOptions: IPSelectableOption[] = [
  { key: 1, text: '1' },
  { key: 2, text: '2' },
  { key: 3, text: '3' },
  { key: 4, text: '4' },
  { key: 5, text: '5' },
  { key: 6, text: '6' },
  { key: 7, text: '7' },
  { key: 8, text: '8' },
  { key: 9, text: '9' },
  { key: 10, text: '10' },
  { key: 11, text: '11' },
  { key: 12, text: '12' },
];

interface IDaySelection {
  key: number;
  text: string;
  value: string;
}

interface IRecurrenceModalProps {
  isOpen: boolean;
  initToAuditYear: boolean;
  minimumSchedule?: IntegrationMinimumSchedule;
  bypassInit?: boolean;
  recurring?: RecurringPattern;
  hideStartDate?: boolean;
  hideYearOptions?: boolean;
  // The "only allow work from the start date" option, only for surfaces where the server enforces it
  showPreventBeforeStart?: boolean;
  // Opt-in for surfaces where "no recurrence" is a valid state (the audit series). Tasks and
  // integrations always need a pattern, so they leave it out.
  allowRemove?: boolean;
  // Needed when this modal opens on top of an open Panel: a non-blocking modal inside a Panel closes
  // on any click, including clicks on its own fields.
  isBlocking?: boolean;
  toggleModal: () => void;
  changeRecurring: (recurring: RecurringPattern) => void;
}

const getRepeatIntervalRank = (type: repeatIntervalType): number => {
  switch (type) {
    case weekly:
      return 2;
    case 'absoluteMonthly':
    case 'relativeMonthly':
      return 3;
    case 'absoluteYearly':
    case 'relativeYearly':
    case 'absoluteYearCycle':
    case 'relativeYearCycle':
    default:
      return 4;
  }
};

const getMinimumScheduleRank = (minimumSchedule?: IntegrationMinimumSchedule): number => {
  switch (minimumSchedule) {
    case IntegrationMinimumSchedule.Daily:
      return 2;
    case IntegrationMinimumSchedule.Monthly:
      return 3;
    default:
      return 2;
  }
};

const getDefaultWeeklyDay = (pattern: RecurringPattern): days => {
  return weekDays1[(pattern.startDate.getDay() + 6) % 7];
};

const isWeekDay = (value: string): value is days => {
  return (weekDays1 as string[]).includes(value);
};

const normalizeWeeklyDaySelection = (
  daySelect: string[],
  minimumSchedule?: IntegrationMinimumSchedule,
  fallbackDay?: days,
): string[] => {
  const selectedDays = daySelect.filter(isWeekDay);
  const resolvedFallbackDay = fallbackDay && weekDays1.includes(fallbackDay) ? fallbackDay : weekDays1[0];

  if (selectedDays.length === 0) {
    return [resolvedFallbackDay];
  }

  if (minimumSchedule !== IntegrationMinimumSchedule.Weekly) {
    return selectedDays;
  }

  const firstSortedSelectedDay = weekDays1.find((day) => selectedDays.includes(day));

  return [firstSortedSelectedDay ?? resolvedFallbackDay];
};

const normalizeRecurringPattern = (
  pattern: RecurringPattern,
  minimumSchedule?: IntegrationMinimumSchedule,
): RecurringPattern => {
  const normalizedPattern = pattern.clone();
  const minimumRank = getMinimumScheduleRank(minimumSchedule);

  if (normalizedPattern.repeatInterval === weekly) {
    normalizedPattern.daySelect = normalizeWeeklyDaySelection(
      normalizedPattern.daySelect,
      minimumSchedule,
      getDefaultWeeklyDay(normalizedPattern),
    );
  }

  if (getRepeatIntervalRank(normalizedPattern.repeatInterval) >= minimumRank) {
    return normalizedPattern;
  }

  const originalStartDate = new Date(normalizedPattern.startDate);
  const originalIsActive = normalizedPattern.isActive;

  if (minimumSchedule === IntegrationMinimumSchedule.Monthly) {
    normalizedPattern.initRecurringPatternMonth(1);
  } else {
    normalizedPattern.initRecurringPatternWeek();
  }

  normalizedPattern.startDate = originalStartDate;
  normalizedPattern.isActive = originalIsActive;

  return normalizedPattern;
};

const RecurrenceModal = (props: IRecurrenceModalProps) => {
  const { t } = useTranslation(['translation', 'control', 'dateTimeComponent']);
  const appContext = useContext(AppContext);

  const getRepeatIntervalOptionsKey = (type: repeatIntervalType): string => {
    switch (type) {
      case 'weekly':
        return 'week';
      case 'absoluteMonthly':
      case 'relativeMonthly':
        return 'month';
      case 'absoluteYearly':
      case 'relativeYearly':
        return 'year';
      case 'absoluteYearCycle':
      case 'relativeYearCycle':
        return 'cycle';
    }
  };

  const getRepeatIntervalFromOptionsKey = (key: string): repeatIntervalType => {
    switch (key) {
      case 'week':
        return 'weekly';
      case 'month':
        return 'absoluteMonthly';
      case 'year':
        return 'absoluteYearly';
      case 'cycle':
        return 'absoluteYearCycle';
      default:
        return 'weekly';
    }
  };

  const getRepeatIntervalOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [];

    if (getMinimumScheduleRank(props.minimumSchedule) <= getRepeatIntervalRank(weekly)) {
      options.push({ key: 'week', text: t('control:TabDetails.Fields.Recurring.Weeks') });
    }

    if (getMinimumScheduleRank(props.minimumSchedule) <= getRepeatIntervalRank('absoluteMonthly')) {
      options.push({ key: 'month', text: t('control:TabDetails.Fields.Recurring.Months') });
    }

    if (!props.hideYearOptions) {
      options.push({ key: 'year', text: t('control:TabDetails.Fields.Recurring.Years') });
      options.push({ key: 'cycle', text: t('control:TabDetails.Fields.Recurring.YearCycle') });
    }

    return options;
  };

  const daySelectionOptions: IDaySelection[] = weekDays1.map((day, idx) => {
    let text = ' ';
    if (day === 'Monday') text = t('control:TabDetails.Fields.Recurring.Monday');
    if (day === 'Tuesday') text = t('control:TabDetails.Fields.Recurring.Tuesday');
    if (day === 'Wednesday') text = t('control:TabDetails.Fields.Recurring.Wednesday');
    if (day === 'Thursday') text = t('control:TabDetails.Fields.Recurring.Thursday');
    if (day === 'Friday') text = t('control:TabDetails.Fields.Recurring.Friday');
    if (day === 'Saturday') text = t('control:TabDetails.Fields.Recurring.Saturday');
    if (day === 'Sunday') text = t('control:TabDetails.Fields.Recurring.Sunday');

    return { key: idx, text: text[0], value: day };
  });

  const [recurring, setRecurring] = useState<RecurringPattern>(new RecurringPattern());

  useEffect(() => {
    // initialize a new Recurrence pattern when there is non or a non-active
    let recurringClone: RecurringPattern = new RecurringPattern();

    if (!props.recurring || (!props.bypassInit && props.recurring.isActive === false)) {
      recurringClone.initRecurringPatternWeek();

      if (props.initToAuditYear) {
        //set the start date to the audit start date for the current year
        const auditStart: Date = getAuditStart(appContext);
        recurringClone.startDate = auditStart;
      } else if (props.recurring) {
        //set start date to start of the recurrence pattern in props
        recurringClone.startDate = props.recurring.startDate;
      }
    } else {
      recurringClone = props.recurring.clone();
    }

    setRecurring(normalizeRecurringPattern(recurringClone, props.minimumSchedule));
  }, [
    props.recurring,
    appContext.globalDataCache.settings,
    props.initToAuditYear,
    appContext,
    props.bypassInit,
    props.minimumSchedule,
  ]);

  const dateChangeHandler = (date: Date | undefined) => {
    if (date) {
      recurring.startDate = date;
      setRecurring(recurring.clone());
    }
  };

  const dropDownChangeHandler = (selectedOption: IPSelectableOption | undefined, field: string) => {
    const key: string = selectedOption?.key as string;
    const newRecurring = recurring.clone();
    switch (field) {
      case 'repeat': {
        newRecurring.repeat = Number.parseInt(key);
        break;
      }
      case 'repeatInterval': {
        if (key === 'cycle') {
          newRecurring.repeat = 3;
          newRecurring.setYearSelect(0, true);
        }

        const repeatInterval = getRepeatIntervalFromOptionsKey(key);
        newRecurring.repeatInterval = repeatInterval;

        if (repeatInterval === weekly) {
          newRecurring.daySelect = normalizeWeeklyDaySelection(
            newRecurring.daySelect,
            props.minimumSchedule,
            getDefaultWeeklyDay(newRecurring),
          );
        }

        break;
      }
    }
    setRecurring(normalizeRecurringPattern(newRecurring, props.minimumSchedule));
  };

  const handleDaySelect = (item: string) => {
    const nextRecurring = recurring.clone();
    let nextDaySelect: string[] = [...nextRecurring.daySelect];

    if (props.minimumSchedule === IntegrationMinimumSchedule.Weekly) {
      nextRecurring.daySelect = normalizeWeeklyDaySelection(
        [item],
        props.minimumSchedule,
        isWeekDay(item) ? item : getDefaultWeeklyDay(nextRecurring),
      );
      setRecurring(nextRecurring);

      return;
    }

    if (nextRecurring.daySelect.includes(item)) {
      if (nextRecurring.daySelect.length > 1) {
        nextDaySelect = nextDaySelect.filter((day: string) => day !== item);
      }
    } else {
      nextDaySelect.push(item);
    }

    nextRecurring.daySelect = normalizeWeeklyDaySelection(
      nextDaySelect,
      props.minimumSchedule,
      getDefaultWeeklyDay(nextRecurring),
    );
    setRecurring(nextRecurring);
  };

  const renderDays = (item: IDaySelection) => {
    return (
      <div
        key={item.key}
        className={[
          contentStyles.daySelectionContainer,
          recurring.daySelect.length > 0 && recurring.daySelect.includes(item.value) ? contentStyles.active : '',
        ].join(' ')}
        onClick={() => handleDaySelect(item.value)}
      >
        {item.text}
      </div>
    );
  };

  const handleSubmit = () => {
    const nextRecurring = normalizeRecurringPattern(recurring, props.minimumSchedule);

    if (props.hideStartDate === true) {
      //if start date is hidden, set it to now so that the pattern starts immediately.
      nextRecurring.startDate = new Date();
    }

    //saving the modal is what activates the pattern; Remove is the opposite action
    nextRecurring.isActive = true;

    props.changeRecurring(nextRecurring);
    props.toggleModal();
  };

  const handleRemove = () => {
    //keep the field values so re-opening the modal still shows the last pattern; only the active
    //flag goes. An inactive pattern serializes to '' which the API reads as "no recurrence".
    const nextRecurring = recurring.clone();
    nextRecurring.isActive = false;

    props.changeRecurring(nextRecurring);
    props.toggleModal();
  };

  const handleCancel = () => {
    props.toggleModal();
  };

  const dropdownStylesTimeOptions: Partial<IPDropdownCustomStyles> = { dropdown: { minWidth: '145px' } };

  const getMaxDate = (): Date => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    return oneYearFromNow;
  };

  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={handleCancel}
      onSave={handleSubmit}
      title={t('control:TabDetails.Fields.Recurring.ModalTitle')}
      height={560}
      width={430}
      isBlocking={props.isBlocking}
      optionalButtons={
        props.allowRemove && recurring.isActive ? (
          <IPDefaultButton onClick={handleRemove} text={t('control:TabDetails.Fields.Recurring.Remove')} />
        ) : undefined
      }
    >
      <Separator />
      <IPStack horizontal tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 10, height: '100%' } }}>
        {!props.hideStartDate && (
          <IPStack.Item>
            <IPStack tokens={globalStackTokensGapLargePlus5}>
              <IPStack.Item>
                <IPLabel>{t('control:TabDetails.Fields.Recurring.Start')}</IPLabel>
              </IPStack.Item>
              <IPStack.Item>
                <IPFontIcon iconName="Sync" />
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        )}
        <IPStack verticalFill tokens={globalStackTokensGapMedium}>
          <IPStack.Item grow>
            <IPStack tokens={globalStackTokensGapMedium}>
              {/* Row 1: Start label + DatePicker */}
              {!props.hideStartDate && (
                <IPStack horizontal tokens={globalStackTokensGapMedium} verticalAlign="center">
                  <Stack.Item grow>
                    <LocalizedDatePicker
                      showMonthPickerAsOverlay={false}
                      showWeekNumbers={true}
                      value={recurring.startDate}
                      onDateChange={dateChangeHandler}
                      maxDate={getMaxDate()}
                    />
                  </Stack.Item>
                </IPStack>
              )}
              {/* Row 2: Sync icon + Repeat label + Repeat dropdown + Interval dropdown */}
              <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                <Stack.Item>
                  <IPLabel>{t('control:TabDetails.Fields.Recurring.Repeat')}</IPLabel>
                </Stack.Item>
                <Stack.Item>
                  <IPDropdown
                    options={repeatOptions}
                    styles={{ dropdown: { minWidth: '70px' }, listbox: { maxHeight: 250 } }}
                    disabled={
                      recurring.repeatInterval === absoluteYearCycle || recurring.repeatInterval === relativeYearCycle
                    }
                    selectedKey={recurring.repeat}
                    onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption) =>
                      dropDownChangeHandler(option, 'repeat')
                    }
                  />
                </Stack.Item>
                <Stack.Item>
                  <IPDropdown
                    options={getRepeatIntervalOptions()}
                    styles={dropdownStylesTimeOptions}
                    selectedKey={getRepeatIntervalOptionsKey(recurring.repeatInterval)}
                    onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption) =>
                      dropDownChangeHandler(option, 'repeatInterval')
                    }
                  />
                </Stack.Item>
              </IPStack>
              {/* Row 3: Empty space + Day selection (if weekly) */}
              {recurring.repeatInterval === weekly && (
                <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                  {daySelectionOptions.map((day: IDaySelection) => renderDays(day))}
                </IPStack>
              )}
              {/* Year cycle summary */}
              {(recurring.repeatInterval === absoluteYearCycle || recurring.repeatInterval === relativeYearCycle) && (
                <RecurringSummary
                  stackStyles={{ root: { maxWidth: 300 } }}
                  variant="small"
                  pattern={recurring}
                  onChange={(year, checked) => {
                    const newRecurring = recurring.clone();
                    newRecurring.setYearSelect(year, checked);
                    setRecurring(newRecurring);
                  }}
                  summaryStyle={RecurringPatternSummaryStyle.interactive}
                />
              )}
              {/* Relative options for non-weekly */}
              {recurring.repeatInterval !== weekly && !props.hideStartDate && (
                <RecurringRelativeOptions recurring={recurring} onChange={setRecurring} />
              )}
              {/* Always show summary, prefixed by empty space for alignment */}
              <RecurringSummary
                textStyles={{ root: { maxWidth: 300 } }}
                variant="small"
                pattern={recurring}
                summaryStyle={RecurringPatternSummaryStyle.text}
              />
              {props.showPreventBeforeStart === true && (
                <IPStack tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <IPCheckbox
                      label={t('control:TabDetails.Fields.Recurring.PreventBeforeStart')}
                      checked={recurring.preventBeforeStart}
                      onChange={(ev, checked) => {
                        const newRecurring = recurring.clone();
                        newRecurring.preventBeforeStart = checked === true;
                        setRecurring(newRecurring);
                      }}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPText variant="small">{t('control:TabDetails.Fields.Recurring.PreventBeforeStartHelp')}</IPText>
                  </IPStack.Item>
                </IPStack>
              )}
            </IPStack>
          </IPStack.Item>
          {props.hideStartDate === true && (
            <IPStack.Item>
              <InfoMessage message={t('control:TabDetails.Fields.Recurring.RestartOnSaveInfo')} />
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack>
    </GenericModal>
  );
};

export default RecurrenceModal;
